import { useState, useEffect } from 'react';
import { Save, Loader2, MessageCircle, Mail, Building2, Home, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { CompanyLogoUpload } from '@/components/settings/CompanyLogoUpload';
import { ChangePasswordForm } from '@/components/settings/ChangePasswordForm';
import { UserProfileCard } from '@/components/settings/UserProfileCard';
import { fetchCNPJ, formatCNPJ, formatPhone, formatCEP } from '@/lib/cnpj';
import { fetchAddress } from '@/lib/viacep';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';

interface Company {
  id: string;
  name: string;
  trade_name: string | null;
  tax_id: string;
  pix_key: string | null;
  logo_url: string | null;
  webhook_url: string | null;
}

const Configuracoes = () => {
  const queryClient = useQueryClient();
  const [fetchingCNPJ, setFetchingCNPJ] = useState(false);
  const [fetchingCEP, setFetchingCEP] = useState(false);
  const [formData, setFormData] = useState({
    // WhatsApp
    wppNumero: '',
    wppToken: '',
    // Email
    emailHost: '',
    emailPort: '',
    emailUser: '',
    emailPass: '',
    // Bank
    banco: '',
    bancoToken: '',
    bancoSecret: '',
    // Company
    nome: '',
    tradeName: '',
    cnpj: '',
    pixKey: '',
    fone: '',
    email: '',
    cep: '',
    rua: '',
    numero: '',
    estado: '',
    cidade: '',
    bairro: '',
    complemento: '',
    logoUrl: ''
  });

  const { data: company, isLoading } = useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return null;

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.company_id)
        .single();

      if (error) throw error;
      return data as Company;
    }
  });

  useEffect(() => {
    if (company) {
      setFormData(prev => ({
        ...prev,
        nome: company.name || '',
        tradeName: company.trade_name || '',
        cnpj: formatCNPJ(company.tax_id || ''),
        pixKey: company.pix_key || '',
        logoUrl: company.logo_url || ''
      }));
    }
  }, [company]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Validate required fields
      if (!formData.nome.trim()) {
        throw new Error('Razão Social é obrigatória');
      }
      if (!formData.cnpj.replace(/\D/g, '')) {
        throw new Error('CNPJ é obrigatório');
      }

      let companyId = company?.id;

      // If no company exists, create one
      if (!companyId) {
        const { data: newCompany, error: createError } = await supabase
          .from('companies')
          .insert({
            name: formData.nome.trim(),
            trade_name: formData.tradeName.trim() || null,
            tax_id: formData.cnpj.replace(/\D/g, ''),
            pix_key: formData.pixKey.trim() || null,
            logo_url: formData.logoUrl || null
          })
          .select()
          .single();

        if (createError) throw createError;
        companyId = newCompany.id;

        // Associate company with user profile
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ company_id: companyId })
          .eq('user_id', user.id);

        if (profileError) throw profileError;
      } else {
        // Update existing company
        const { error } = await supabase
          .from('companies')
          .update({
            name: formData.nome.trim(),
            trade_name: formData.tradeName.trim() || null,
            tax_id: formData.cnpj.replace(/\D/g, ''),
            pix_key: formData.pixKey.trim() || null,
            logo_url: formData.logoUrl || null
          })
          .eq('id', companyId);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast({
        title: 'Configurações salvas',
        description: 'As alterações foram salvas com sucesso.'
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: err.message,
        variant: 'destructive'
      });
    }
  });

  const handleChange = (field: string, value: string) => {
    // Apply formatters for specific fields
    if (field === 'cnpj') {
      value = formatCNPJ(value);
    } else if (field === 'fone') {
      value = formatPhone(value);
    } else if (field === 'cep') {
      value = formatCEP(value);
      // Auto-fetch address when CEP is complete
      const cleanCEP = value.replace(/\D/g, '');
      if (cleanCEP.length === 8) {
        handleFetchCEP(cleanCEP);
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (url: string) => {
    setFormData(prev => ({ ...prev, logoUrl: url }));
  };

  const handleFetchCEP = async (cep: string) => {
    setFetchingCEP(true);
    try {
      const data = await fetchAddress(cep);
      if (data) {
        setFormData(prev => ({
          ...prev,
          rua: data.logradouro || prev.rua,
          complemento: data.complemento || prev.complemento,
          bairro: data.bairro || prev.bairro,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado,
        }));
        toast({
          title: 'Endereço encontrado',
          description: 'Os campos de endereço foram preenchidos automaticamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setFetchingCEP(false);
    }
  };

  const handleFetchCNPJ = async () => {
    const cleanCNPJ = formData.cnpj.replace(/\D/g, '');
    
    if (cleanCNPJ.length !== 14) {
      toast({
        title: 'CNPJ inválido',
        description: 'Digite um CNPJ válido com 14 dígitos.',
        variant: 'destructive'
      });
      return;
    }

    setFetchingCNPJ(true);

    try {
      const data = await fetchCNPJ(cleanCNPJ);
      
      if (data) {
        setFormData(prev => ({
          ...prev,
          nome: data.nome || prev.nome,
          tradeName: data.fantasia || prev.tradeName,
          rua: data.logradouro || prev.rua,
          numero: data.numero || prev.numero,
          complemento: data.complemento || prev.complemento,
          bairro: data.bairro || prev.bairro,
          cidade: data.municipio || prev.cidade,
          estado: data.uf || prev.estado,
          cep: formatCEP(data.cep) || prev.cep,
          fone: formatPhone(data.telefone) || prev.fone,
          email: data.email || prev.email
        }));

        toast({
          title: 'Dados encontrados',
          description: 'As informações da empresa foram preenchidas automaticamente.'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao buscar CNPJ',
        description: error.message || 'Não foi possível consultar o CNPJ.',
        variant: 'destructive'
      });
    } finally {
      setFetchingCNPJ(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Company Name and Logo */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {formData.logoUrl ? (
            <img 
              src={formData.logoUrl} 
              alt="Logo da empresa" 
              className="size-12 rounded-lg object-cover border"
            />
          ) : (
            <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="size-6 text-primary" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {formData.tradeName || formData.nome || 'Configurações'}
            </h1>
            <p className="text-muted-foreground">Personalize o sistema de cobrança</p>
          </div>
        </div>
        <Button 
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || isLoading}
          className="gap-2 hover-pop"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>

      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="empresa">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="empresa" className="gap-2">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Empresa</span>
            </TabsTrigger>
            <TabsTrigger value="conta" className="gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Minha Conta</span>
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="gap-2">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">E-mail</span>
            </TabsTrigger>
            <TabsTrigger value="banco" className="gap-2">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Banco</span>
            </TabsTrigger>
          </TabsList>

          {/* Empresa Tab */}
          <TabsContent value="empresa" className="mt-6">
            <div className="bg-card rounded-lg border p-6 space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <Home className="size-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">Configuração da Empresa</h2>
                  <p className="text-sm text-muted-foreground">
                    Preencha o CNPJ para buscar os dados automaticamente.
                  </p>
                </div>
              </div>

              {/* CNPJ with auto-fetch - First */}
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cnpj"
                        value={formData.cnpj}
                        onChange={(e) => handleChange('cnpj', e.target.value)}
                        placeholder="00.000.000/0001-00"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleFetchCNPJ}
                        disabled={fetchingCNPJ || formData.cnpj.replace(/\D/g, '').length !== 14}
                        className="gap-2 shrink-0"
                      >
                        {fetchingCNPJ ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                        Buscar
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Clique em "Buscar" para preencher automaticamente os dados da empresa.
                    </p>
                  </div>
                </div>
              </div>

              {/* Logo and Name */}
              <div className="flex items-start gap-4">
                {company ? (
                  <CompanyLogoUpload
                    companyId={company.id}
                    currentLogoUrl={formData.logoUrl}
                    onLogoChange={handleLogoChange}
                  />
                ) : (
                  <div className="relative size-24 flex items-center justify-center border-2 border-dashed border-muted rounded-xl bg-muted/50">
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <Building2 className="size-8" />
                      <span className="text-[10px] font-medium text-center">Crie a empresa primeiro</span>
                    </div>
                  </div>
                )}
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Razão Social</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => handleChange('nome', e.target.value)}
                      placeholder="Nome da empresa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tradeName">Nome Fantasia</Label>
                    <Input
                      id="tradeName"
                      value={formData.tradeName}
                      onChange={(e) => handleChange('tradeName', e.target.value)}
                      placeholder="Nome fantasia"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fone">Telefone</Label>
                  <Input
                    id="fone"
                    value={formData.fone}
                    onChange={(e) => handleChange('fone', e.target.value)}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contato@empresa.com"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Endereço</p>
                <div className="grid grid-cols-6 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP</Label>
                    <div className="relative">
                      <Input
                        id="cep"
                        value={formData.cep}
                        onChange={(e) => handleChange('cep', e.target.value)}
                        placeholder="00000-000"
                        className={fetchingCEP ? "pr-8" : ""}
                      />
                      {fetchingCEP && (
                        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 col-span-4">
                    <Label htmlFor="rua">Logradouro</Label>
                    <Input
                      id="rua"
                      value={formData.rua}
                      onChange={(e) => handleChange('rua', e.target.value)}
                      placeholder="Rua, Av, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero">Nº</Label>
                    <Input
                      id="numero"
                      value={formData.numero}
                      onChange={(e) => handleChange('numero', e.target.value)}
                      placeholder="123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado</Label>
                    <select
                      id="estado"
                      value={formData.estado}
                      onChange={(e) => handleChange('estado', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">UF</option>
                      {ESTADOS_BRASILEIROS.map(estado => (
                        <option key={estado.sigla} value={estado.sigla}>{estado.sigla}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={formData.cidade}
                      onChange={(e) => handleChange('cidade', e.target.value)}
                      placeholder="Cidade"
                    />
                  </div>
                  <div className="space-y-2 col-span-3">
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={formData.bairro}
                      onChange={(e) => handleChange('bairro', e.target.value)}
                      placeholder="Bairro"
                    />
                  </div>
                  <div className="space-y-2 col-span-full">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={formData.complemento}
                      onChange={(e) => handleChange('complemento', e.target.value)}
                      placeholder="Apto, Sala, etc."
                    />
                  </div>
                </div>
              </div>

              {/* PIX Key */}
              <div className="space-y-2">
                <Label htmlFor="pixKey">Chave PIX</Label>
                <Input
                  id="pixKey"
                  value={formData.pixKey}
                  onChange={(e) => handleChange('pixKey', e.target.value)}
                  placeholder="email@empresa.com ou CNPJ"
                />
              </div>
            </div>
          </TabsContent>

          {/* Minha Conta Tab */}
          <TabsContent value="conta" className="mt-6">
            <div className="bg-card rounded-lg border p-6 space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <User className="size-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">Minha Conta</h2>
                  <p className="text-sm text-muted-foreground">
                    Gerencie suas informações pessoais e segurança.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* User Info Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider pb-2 border-b">
                    Informações do Usuário
                  </h3>
                  <UserProfileCard />
                </div>

                {/* Password Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider pb-2 border-b">
                    Alterar Senha
                  </h3>
                  <ChangePasswordForm />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp" className="mt-6">
            <div className="bg-card rounded-lg border p-6 space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <MessageCircle className="size-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">Integração WhatsApp</h2>
                  <p className="text-sm text-muted-foreground">
                    Configure o número oficial para envio de mensagens automáticas.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="wppNumero">Número do WhatsApp</Label>
                  <Input
                    id="wppNumero"
                    value={formData.wppNumero}
                    onChange={(e) => handleChange('wppNumero', e.target.value)}
                    placeholder="+55 11 99999-9999"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wppToken">API Token</Label>
                  <Input
                    id="wppToken"
                    type="password"
                    value={formData.wppToken}
                    onChange={(e) => handleChange('wppToken', e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="mt-6">
            <div className="bg-card rounded-lg border p-6 space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <Mail className="size-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">Servidor de E-mail (SMTP)</h2>
                  <p className="text-sm text-muted-foreground">
                    Dados para o envio de notificações e boletos por e-mail.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emailHost">Host SMTP</Label>
                  <Input
                    id="emailHost"
                    value={formData.emailHost}
                    onChange={(e) => handleChange('emailHost', e.target.value)}
                    placeholder="smtp.exemplo.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailPort">Porta</Label>
                  <Input
                    id="emailPort"
                    type="number"
                    value={formData.emailPort}
                    onChange={(e) => handleChange('emailPort', e.target.value)}
                    placeholder="587"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailUser">Usuário</Label>
                  <Input
                    id="emailUser"
                    value={formData.emailUser}
                    onChange={(e) => handleChange('emailUser', e.target.value)}
                    placeholder="usuario@exemplo.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailPass">Senha</Label>
                  <Input
                    id="emailPass"
                    type="password"
                    value={formData.emailPass}
                    onChange={(e) => handleChange('emailPass', e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Banco Tab */}
          <TabsContent value="banco" className="mt-6">
            <div className="bg-card rounded-lg border p-6 space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b">
                <Building2 className="size-6 text-primary" />
                <div>
                  <h2 className="text-lg font-bold">Configuração Bancária</h2>
                  <p className="text-sm text-muted-foreground">
                    Conecte sua conta para geração automática de cobranças.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="banco">Banco Integrado</Label>
                  <select
                    id="banco"
                    value={formData.banco}
                    onChange={(e) => handleChange('banco', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Selecione um banco</option>
                    <option value="1">Banco do Brasil</option>
                    <option value="2">Itaú</option>
                    <option value="3">Santander</option>
                    <option value="4">Inter</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bancoToken">Client ID / Token</Label>
                    <Input
                      id="bancoToken"
                      type="password"
                      value={formData.bancoToken}
                      onChange={(e) => handleChange('bancoToken', e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bancoSecret">Client Secret</Label>
                    <Input
                      id="bancoSecret"
                      type="password"
                      value={formData.bancoSecret}
                      onChange={(e) => handleChange('bancoSecret', e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Configuracoes;
